// 🎞 **원쉐이프 제품 필름 — 입력 데이터 계약**(2026-09-28 · 모션 스타일 재편 2단계 · 정본 지도 `docs/01-plan/features/motion-style-pack.md` §9).
//
// ★흐름·음악·길이·상태 키 표(K)·커서 키는 **고정 템플릿**이다(회장님 09-28 재논의 금지)★ — 여기 실리는 것은 «바뀌는 데이터»뿐:
//   브랜드색·로고·서비스명·주소, 입력창 문구, 주제 3개, 대본 3줄, 장면 6장, 완성 영상 프레임 묶음.
// ★그리기는 시간의 순수 함수★ — 이미지는 **미리 decode 된** `CanvasImageSource` 로 받는다. 그리는 쪽이 fetch·seek 를 하면
//   같은 t 가 다른 픽셀을 낸다(결정성 계약). 프레임 묶음(30fps)도 그래서 «배열»이다(설계서 3-3 PhonePlay).
// ★3D 층 자리는 비워 둔다★(회장님 09-28 «블렌더+리모션 하이브리드는 반드시» · 별도 레인 ③′) — `layers` 는 지금 아무 것도
//   그리지 않는다. 리모션 컴포지션이 배경 → 3D 뒤층 → 캔버스 → 3D 앞층 순서로 쌓을 때 이 데이터가 그 자리를 안다.

/** 템플릿 고정값 — 16:9 · 16.0초 · 120BPM(박자 0.5초). 참조 렌더 `쇼릴v4/index.html` 과 같다. */
export const ONESHAPE_W = 1920;
export const ONESHAPE_H = 1080;
export const ONESHAPE_DURATION_SEC = 16;
export const ONESHAPE_BEAT_SEC = 0.5;
/** 완성 칸(PhonePlay) 프레임 묶음의 fps 와 구간 길이(11.0~14.4초 · 설계서 10-1 «3.4초 구간 102장»). */
export const ONESHAPE_PHONE_FPS = 30;
export const ONESHAPE_PHONE_SEC = 3.4;
export const ONESHAPE_PHONE_FRAMES = Math.round(ONESHAPE_PHONE_FPS * ONESHAPE_PHONE_SEC); // 102

/** 3D 층 자리(③′ 하이브리드용 · 지금은 비어 있다). 캔버스 층은 이 순서의 `canvas` 자리다. */
export const ONESHAPE_LAYER_ORDER = ["background", "back3d", "canvas", "front3d"] as const;
export type OneShapeLayer = (typeof ONESHAPE_LAYER_ORDER)[number];

/** 3D 층 참조 — 블렌더 투명 WebM 한 편(리모션이 `<Video>` 로 얹는다). 캔버스 층은 이 값을 읽지 않는다. */
export interface OneShapeLayerRef {
  /** 투명 WebM 주소(알파 유지). */
  url: string;
  /** 이 층이 켜지는 구간(초). 없으면 전 구간. */
  from?: number;
  to?: number;
}

export interface OneShapeBrand {
  /** 브랜드색 1개(`#rrggbb`). 공·로더·선택 행·파형·큰 카피 둘째 줄·배경 쓸림에 쓴다. */
  color: string;
  /** 로고 그림(decode 완료). 없으면 로고 타일에 서비스명 첫 글자를 그린다(빈 칸 금지). */
  logo: CanvasImageSource | null;
  /** 서비스명(로고 밑 큰 글자). */
  name: string;
  /** 주소 한 줄(회색 · 작은 글자). 없으면 그리지 않는다. */
  url?: string;
}

export interface OneShapeChip {
  /** 칩 아이콘 글자(«PDF» 등 · 짧게). */
  badge: string;
  /** 칩 이름(«상품설명서» 등). */
  label: string;
}

export interface OneShapeScene {
  /** 장면 그림(decode 완료). 갤러리 대표 프레임 또는 캐릭터 그림. */
  img: CanvasImageSource;
  /** 칸 바탕색(그림 밖 여백). */
  tint: string;
  /** `cover` = 칸을 꽉 채우고 `fy`(0~1) 높이를 보여준다(세로 원본) · `contain` = 다 보이게(정사각 캐릭터 그림). */
  fit: "cover" | "contain";
  /** cover 일 때 보여줄 높이 위치(0 = 위 · 1 = 아래). 1차는 고정값(0.3~0.45 · 설계서 10-3). */
  fy?: number;
  /** 어디서 왔나 — 가드·보고용(그리기는 읽지 않는다). */
  source: "video" | "character";
  /** 이 그림이 속한 테넌트(캐릭터 그림은 공용이라 null). 가드가 `tenantId` 와 대조한다. */
  tenantId: string | null;
}

export interface OneShapeFinal {
  /** 30fps 프레임 묶음(decode 완료 · `ONESHAPE_PHONE_FRAMES` 장 권장). 비어 있으면 `hero` 로 대신한다. */
  frames: readonly CanvasImageSource[];
  /** 프레임이 없을 때 폰 화면에 넣을 그림(와카판 방식). */
  hero: CanvasImageSource | null;
  /** 폰 화면 바탕색. */
  tint: string;
  /** 폰 화면 아래 자막 두 줄(와카판 방식 · 없으면 그리지 않는다). */
  caption?: readonly [string, string];
}

/** 그리기 함수가 받는 전부. 검증은 `validateOneShapeData`. */
export interface OneShapeFilmData {
  /** 이 영상의 주인 테넌트 — 장면·프레임의 `tenantId` 가 이것과 다르면 검증이 거절한다(테넌트 경계). */
  tenantId: string;
  brand: OneShapeBrand;
  /** 입력창 안내 문구(타이핑 전). */
  placeholder: string;
  /** 타이핑되는 요청 문구. */
  typed: string;
  /** 날아와 꽂히는 파일 칩. 없으면 칩 없이 타이핑만. */
  chip?: OneShapeChip;
  /** 주제 3개(두 번째가 선택된다 — 템플릿 고정). */
  topics: readonly [string, string, string];
  /** 대본 3줄. */
  script: readonly [string, string, string];
  /** 장면 6칸(3×2 · 타임라인 썸네일도 같은 목록). */
  scenes: readonly OneShapeScene[];
  final: OneShapeFinal;
  /** 3D 층 자리(③′) — 지금은 채우지 않는다. */
  layers?: { back3d?: OneShapeLayerRef | null; front3d?: OneShapeLayerRef | null };
}

const HEX = /^#[0-9a-f]{6}$/i;

/* ── 🎨 브랜드색 대비 가드(09-29 dev 굽기 실측 — 테넌트 설정색 `#dae0ec` 로 공·로더·선택 행·쓸림 배경 위 흰 글자가 안 보였다) ── */
/** 흰 글자·흰 도형이 브랜드색 위에 올라간다(선택 행·쓸림 배경 제목·로더 호) — 흰색 대비 하한. WCAG 큰 글자 기준 3:1. */
export const ONESHAPE_MIN_ACCENT_CONTRAST = 3;
/** 회색·흰색·검정은 브랜드색이 아니다 — 채도(HSV) 하한. */
export const ONESHAPE_MIN_ACCENT_SATURATION = 0.2;

const rgbOf = (hex: string): [number, number, number] => { const n = parseInt(hex.slice(1), 16); return [n >> 16, (n >> 8) & 255, n & 255]; };
const lin8 = (v: number): number => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
/** 흰색과의 대비(WCAG). `#rrggbb` 가 아니면 0. */
export function accentContrastOnWhite(hex: string): number {
  if (!HEX.test(hex)) return 0;
  const [r, g, b] = rgbOf(hex);
  const L = 0.2126 * lin8(r) + 0.7152 * lin8(g) + 0.0722 * lin8(b);
  return 1.05 / (L + 0.05);
}
/**
 * 브랜드색으로 쓸 수 있게 다듬는다 — 채도가 없으면 null(브랜드색이 아니다), 옅으면 **색상은 그대로 두고 어둡게** 해 흰색 대비 3:1 을 맞춘다.
 * 순수 함수(같은 입력 = 같은 색). 호출자는 null 이면 다음 후보(로고 추출색 → 기본 파랑)로 넘어간다.
 */
export function normalizeAccent(hex: string | null | undefined): string | null {
  if (!hex || !HEX.test(hex)) return null;
  let [r, g, b] = rgbOf(hex);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  if (max === 0 || (max - min) / max < ONESHAPE_MIN_ACCENT_SATURATION) return null;
  const toHex = () => `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
  for (let i = 0; i < 40 && accentContrastOnWhite(toHex()) < ONESHAPE_MIN_ACCENT_CONTRAST; i++) { r *= 0.94; g *= 0.94; b *= 0.94; }
  return accentContrastOnWhite(toHex()) >= ONESHAPE_MIN_ACCENT_CONTRAST ? toHex().toLowerCase() : null;
}

const isImg = (v: unknown): v is CanvasImageSource => !!v && typeof v === "object";

/**
 * 데이터 검증 — 빈 칸 0 · 테넌트 경계 · 개수 고정. 문제가 있으면 사람이 읽는 사유 목록(빈 배열 = 통과).
 * ★그리기 함수는 검증하지 않는다★ — 프레임마다 검증하면 비용이고, 조립기(`assets.ts`)가 데이터를 만들 때 한 번 부른다.
 */
export function validateOneShapeData(d: OneShapeFilmData): string[] {
  const errs: string[] = [];
  if (!d.tenantId) errs.push("tenantId 없음");
  if (!HEX.test(d.brand?.color ?? "")) errs.push(`브랜드색이 #rrggbb 가 아님: ${String(d.brand?.color)}`);
  else if (accentContrastOnWhite(d.brand.color) < ONESHAPE_MIN_ACCENT_CONTRAST) errs.push(`브랜드색이 너무 옅음(흰색 대비 ${accentContrastOnWhite(d.brand.color).toFixed(2)} < ${ONESHAPE_MIN_ACCENT_CONTRAST}): ${d.brand.color}`);
  if (!d.brand?.name?.trim()) errs.push("서비스명 없음");
  if (!d.placeholder?.trim()) errs.push("입력창 안내 문구 없음");
  if (!d.typed?.trim()) errs.push("타이핑 문구 없음");
  if (!Array.isArray(d.topics) || d.topics.length !== 3 || d.topics.some((s) => !s?.trim())) errs.push("주제는 빈 칸 없이 3개");
  if (!Array.isArray(d.script) || d.script.length !== 3 || d.script.some((s) => !s?.trim())) errs.push("대본은 빈 칸 없이 3줄");
  if (!Array.isArray(d.scenes) || d.scenes.length !== 6) errs.push(`장면은 6칸(지금 ${Array.isArray(d.scenes) ? d.scenes.length : 0})`);
  else d.scenes.forEach((s, i) => {
    if (!isImg(s?.img)) errs.push(`장면 ${i + 1} 그림 없음`);
    if (s?.source === "video" && s.tenantId !== d.tenantId) errs.push(`장면 ${i + 1} 이 다른 테넌트 자산(${String(s?.tenantId)})`);
    if (s?.fit === "cover" && s.fy !== undefined && !(s.fy >= 0 && s.fy <= 1)) errs.push(`장면 ${i + 1} fy 범위 밖`);
  });
  if (!d.final) errs.push("완성 칸 없음");
  else {
    if (!Array.isArray(d.final.frames)) errs.push("완성 칸 프레임 묶음이 배열이 아님");
    else if (d.final.frames.length === 0 && !isImg(d.final.hero)) errs.push("완성 칸이 비었음(프레임 0장 · 대체 그림 없음)");
    else if (d.final.frames.some((f) => !isImg(f))) errs.push("완성 칸 프레임에 빈 항목");
  }
  return errs;
}
